import { Controller } from './controller';
import { EchartsBuilder } from './echarts-builder';
import { Echarts } from './echarts';
import { loader } from './loader';
import deepmerge from 'deepmerge';

export class ScalerChartBuilder {
    /**
     * Returns the ScalerChartBuilder instance with the given name.
     *
     * @param name The name of the chart to return.
     * @returns The ScalerChartBuilder instance.
     * @throws
     */
    static chart(name) {
        if (name in ScalerChartBuilder.charts) {
            return ScalerChartBuilder.charts[name];
        }
        throw new Error(`Unable to find a chart named '${name}'.`);
    }

    /**
     * Setup the specific provider information.
     *
     * @return {Object}
     */
    setup() {
        return {
            provider: new Echarts(),
            builder: new EchartsBuilder({
                xAxis: {},
                yAxis: {},
                series: [],
            }),
            defaultController: new Controller(),
        };
    }
    /**
     * Stores the ScalerChartBuilder provider.
     *
     * @var {Echarts}
     */
    provider;

    /**
     * Stores the ScalerChartBuilder default controller.
     *
     * @var {EchartsBuilder}
     */
    builder;

    /**
     * Stores the default controller to use in case no controller is
     * attached to the instance.
     *
     * @var {Controller}
     */
    defaultController;

    /**
     * Stores the named ScalerChartBuilder instances.
     *
     * @var {Echarts[]}
     */
    static charts = [];

    /**
     * Stores the chart options.
     *
     * @var {Object}
     */
    options;

    /**
     * Gets the chart controller.
     *
     * @var {Controller}
     */
    get controller() {
        return this.options.controller ?? this.defaultController;
    }

    /**
     * Stores the unsubscribers of the chart.
     */
    unsubscribers = {
        create: undefined,
        update: undefined,
        delete: undefined,
    };

    /**
     * Creates a new ScalerChartBuilder controlled chart.
     *
     * @param options The chart options.
     */
    constructor(options) {
        // Setup the options.
        this.options = { initiate: true, subscribe: true, ...options };

        // Store the chart for later use if there's a name defined.
        if (this.options.name) ScalerChartBuilder.charts[this.options.name] = this;

        // Initialize the provider-specific information.
        const { provider, builder, defaultController } = this.setup();
        this.provider = provider;
        this.builder = builder;
        this.defaultController = defaultController;

        // Initialize ScalerChartBuilder.
        this.initialize();
    }

    /**
     * Initialization of the chart given the current options.
     */
    initialize() {
        this.controller.attach(this);
        if (this.options.element) this.options.element.style.position = 'relative';
        if (this.options.subscribe) this.subscribe();
        if (this.options.initiate && this.isSubscribed()) this.controller.create(this.options);
        this.controller.send('initialized');
    }

    /**
     * Merges the given options with the chart's current configuration.
     *
     * @param configuration
     */
    mergeOptions(configuration) {
        this.options = { ...this.options, ...configuration };
        this.controller.send('options-changed');
    }

    /**
     * Requests the server data.
     *
     * @returns {Promise}
     * @throws {Error}
     */
    async request() {
        if (this.options.url === undefined) {
            throw new Error('There is no chart URL set and an online mode attempt was performed.');
        }

        const response = await (this.options.fetch ?? fetch)(
            this.options.url,
            deepmerge(
                {
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                },
                this.options.options ?? {}
            )
        );
        this.controller.send('requested');

        if (!response.ok) throw new Error('There was an error with the chart request');

        const result = await response.json();
        this.controller.send('decoded');
        return result;
    }

    /**
     * Requests the server data and transforms it into the chart configuration.
     * Respects offline mode. Respects and merges the chart option.
     *
     * @returns {Promise}
     * @throws {Error}
     */
    async requestAndTransform() {
        const configuration = (value) =>
            JSON.parse(JSON.stringify(isBuilder(value) ? value(this.builder.copy()).configuration : value));

        if (this.isOffline()) {
            if (this.options.chart === undefined) {
                throw new Error(
                    'There is no chart defined while offline mode requires it. Please either a url or a chart property.'
                );
            }
            return configuration(this.options.chart);
        }

        const protocol = await this.request();

        if (this.options.chart === undefined) {
            return this.options.transformer?.(protocol) ?? protocol.chart ?? protocol;
        }

        return this.options.serverPriority
            ? deepmerge(configuration(this.options.chart), this.options.transformer?.(protocol) ?? protocol.chart)
            : deepmerge(this.options.transformer?.(protocol) ?? protocol.chart, configuration(this.options.chart));
    }

    /**
     * Creates the provider's chart given the configuration.
     *
     * @param configuration The chart configuration.
     */
    createChart(configuration) {
        if (this.options.element === null) {
            return console.warn('The chart element is null, unable to create the chart');
        }

        if (document?.readyState === 'loading') {
            return console.warn(
                'The document is still loading, please make sure the document is ready by listening to the `DOMContentLoaded` or the `load` event.'
            );
        }

        this.options.beforeCreate?.(configuration);
        this.provider.create(this.options.element, configuration, this.options.options);
        this.options.afterCreate?.(this.instance, configuration);
    }

    /**
     * Updates the provider's chart given the configuration.
     *
     * @param configuration The chart configuration
     */
    updateChart(configuration) {
        this.options.beforeUpdate?.(this.instance, configuration);
        this.provider.update(configuration);
        this.options.afterUpdate?.(this.instance, configuration);
    }

    /**
     * On create event callback.
     *
     * @param event The custom event information.
     */
    onCreate(event) {
        if (event.detail.options?.customConfiguration) {
            this.createChart(event.detail.options?.customConfiguration.configuration);
            this.controller.send('created');
            return;
        }
        if (this.provider.isInitiated()) return;
        if (event.detail) this.mergeOptions(event.detail);
        if (this.isOnline()) this.loading(true);
        if (event.detail.options?.delay) {
            setTimeout(() => {
                this.requestAndTransform().then((configuration) => {
                    if (this.isOnline()) this.loading(false);
                    this.createChart(configuration);
                    this.controller.send('created');
                });
            }, event.detail.options.delay);
            return;
        }
        this.requestAndTransform().then((configuration) => {
            if (this.isOnline()) this.loading(false);
            if (event.detail.options?.onDataLoadedRangeSliderCallback) {
                configuration = event.detail.options.onDataLoadedRangeSliderCallback(configuration);
            }
            if (event.detail.options?.onDataLoadedCallback) {
                configuration = event.detail.options.onDataLoadedCallback(configuration);
            }
            this.createChart(configuration);
            this.controller.send('created');
        });
    }

    /**
     * On update event callback.
     *
     * @param event The custom event information.
     */
    onUpdate(event) {
        if (!this.provider.isInitiated()) return;
        if (event.detail) this.mergeOptions(event.detail);
        if (event.detail?.loading && this.isOnline()) this.loading(true);
        const startedAt = this.provider.instance.getOption().last_update_started_at;
        this.requestAndTransform().then((configuration) => {
            if (event.detail?.loading && this.isOnline()) this.loading(false);
            if (this.options?.options?.onDataLoadedRangeSliderCallback) {
                configuration = this.options?.options?.onDataLoadedRangeSliderCallback(configuration);
            }
            if (this.options?.options?.onDataLoadedCallback) {
                configuration = this.options?.options?.onDataLoadedCallback(configuration);
            }
            configuration.last_update_started_at = startedAt;
            this.updateChart(configuration);
            this.controller.send('updated');
        });
    }

    /**
     * On delete event callback.
     *
     * @param event The custom event information.
     */
    onDelete(_event) {
        if (!this.provider.isInitiated()) return;
        this.options.beforeDelete?.(this.instance);
        this.provider.delete();
        this.options.afterDelete?.();
        this.controller.send('deleted');
    }

    /**
     * Determines if the chart is currently running in offline mode.
     *
     * Offline mode means the chart didn't perform an HTTP request.
     *
     * @returns {boolean}
     */
    isOffline() {
        return this.options.url === undefined;
    }

    /**
     * Determines if the chart is currently running in online mode.
     *
     * Online mode means the chart performed an HTTP request.
     *
     * @returns {boolean}
     */
    isOnline() {
        return !this.isOffline();
    }

    /**
     * Determines if the chart loader is active.
     *
     * @returns {boolean}
     */
    isLoading() {
        return Boolean(this.options.element?.querySelector('.modal'));
    }

    /**
     * Sets the loading state of the chart.
     * Can be used to controll if a loading spinner
     * should be shown or not.
     *
     * @param {boolean} value
     */
    loading(value) {
        const modal = this.options.element?.querySelector('.modal');
        switch (value) {
            case true: {
                if (!modal) {
                    const modal = document.createElement('div');
                    modal.style.width = '100%';
                    modal.style.height = '100%';
                    modal.style.display = 'flex';
                    modal.style.alignItems = 'center';
                    modal.style.justifyContent = 'center';
                    modal.style.position = 'absolute';
                    modal.style.top = '0px';
                    modal.style.bottom = '0px';
                    modal.style.left = '0px';
                    modal.style.right = '0px';
                    modal.style.zIndex = '10';
                    modal.style.backdropFilter = `blur(${this.options.loader?.blur ?? 3}px)`;
                    modal.classList.add('modal');
                    if (this.options.loader?.html) {
                        if (typeof this.options.loader.html === 'string') {
                            modal.innerHTML = this.options.loader.html;
                        } else {
                            modal.append(this.options.loader.html);
                        }
                    } else {
                        modal.innerHTML = loader(this.options.loader);
                    }
                    this.options.element?.append(modal);
                    this.controller.send('loading-enabled');
                }
            }
            case false: {
                if (modal) {
                    this.options.element?.removeChild(modal);
                    this.controller.send('loading-disabled');
                }
            }
        }
    }

    /**
     * Determines if the chart is currently subscribed
     * to the controller.
     *
     * @returns {boolean}
     */
    isSubscribed() {
        return (
            this.unsubscribers.create !== undefined &&
            this.unsubscribers.update !== undefined &&
            this.unsubscribers.delete !== undefined
        );
    }

    /**
     * Subscribes to the controller in order to execute the orders.
     *
     * @param unsubscribe Unsubscribes from any previous subscriptions (if any).
     */
    subscribe(unsubscribe = true) {
        // Unsubscribe any previous subscriptions
        if (unsubscribe) this.unsubscribe();
        // Create the subscription handlers.
        const onCreate = (event) => this.onCreate(event);
        const onUpdate = (event) => this.onUpdate(event);
        const onDelete = (event) => this.onDelete(event);
        // Create a new subscription to the current instance.
        this.unsubscribers.create = this.controller.on('create', onCreate);
        this.unsubscribers.update = this.controller.on('update', onUpdate);
        this.unsubscribers.delete = this.controller.on('delete', onDelete);
        this.controller.send('subscribed');
    }

    /**
     * Unsubscribes from the controller if it was previously subscribed.
     */
    unsubscribe() {
        this.unsubscribers.create?.();
        this.unsubscribers.update?.();
        this.unsubscribers.delete?.();
        this.controller.send('unsubscribed');
    }

    /**
     * Returns the native instance of the chart.
     *
     * @returns {Object}
     */
    get instance() {
        return this.provider.instance;
    }

    /**
     * Creates the chart with the given options.
     *
     * @param detail The options to create the chart with.
     */
    create(detail) {
        this.controller.create(detail);
    }

    /**
     * Updates the chart with the given options.
     *
     * @param detail The options to update the chart with.
     */
    update(detail) {
        this.controller.update(detail);
    }

    /**
     * Deletes the current chart.
     */
    delete() {
        this.controller.delete();
    }

    /**
     * Determines if the chart is currently initialized.
     *
     * @returns {boolean}
     */
    isInitiated() {
        return this.provider.isInitiated();
    }
}

if (typeof window !== 'undefined' && window.ScalerChartBuilder === undefined)
    window.ScalerChartBuilder = ScalerChartBuilder;
